// Single shared poller for "my chats" (GET /api/practice/chats), consumed by
// the incoming-call banner, Browse's calling card, and Browse's unread badges.
// Before this, each of those polled the same endpoint independently — on the
// Browse page alone that was 3 simultaneous redundant fetches every 5-6s per
// user, which was enough to exhaust prod's small DB connection pool once
// combined across ~20 concurrent students.
//
// The incoming-call ring/dismiss state lives here too (not in a per-consumer
// hook) so it's genuinely shared: dismissing it on one surface hides it on
// every other surface, and it only rings once even though both the global
// banner and Browse's inline card read the same call.
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { usePracticeAuth } from "./PracticeAuthContext";
import { usePolling } from "../feature-module/speaking-partner/hooks/usePolling";

const API = process.env.REACT_APP_API_BASE_URL;

export interface PracticeChatSummary {
  id: number;
  otherUser: { id: number; displayName: string; online: boolean } | null;
  lastMessageAt: string | null;
  unreadCount: number;
  callActive: boolean;
  callStartedBy: number | null;
  // Set when someone already on a call added me from this chat's Inbox —
  // rides this chat's own poll instead of needing a separate poll loop.
  groupCallInvite: { hostChatId: number; hostDisplayName: string } | null;
}

// Normalizes a direct 1:1 ring and a group-call invite into one shape so
// IncomingCallBanner / Browse's calling card don't need to branch on which
// kind fired — they just read displayName/joinPath.
export interface IncomingCallInfo {
  chatId: number;
  displayName: string;
  joinPath: string;
  isGroup: boolean;
}

interface PracticeChatsContextType {
  chats: PracticeChatSummary[];
  refresh: () => Promise<void>;
  incomingCall: IncomingCallInfo | null;
  dismissIncomingCall: () => void;
}

const PracticeChatsContext = createContext<PracticeChatsContextType | undefined>(undefined);

// Two short beeps via Web Audio API — no external asset to manage/license,
// works offline. Browsers block audio until the user has interacted with the
// page at least once; that's a browser policy, not something to work around.
const playRing = () => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioCtx();
    const beep = (startOffset: number) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(660, ctx.currentTime + startOffset);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + startOffset);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + startOffset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startOffset + 0.35);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(ctx.currentTime + startOffset);
      oscillator.stop(ctx.currentTime + startOffset + 0.35);
    };
    beep(0);
    beep(0.45);
    setTimeout(() => ctx.close(), 1200);
  } catch {
    // autoplay blocked or unsupported — fail silently
  }
};

export const PracticeChatsProvider = ({ children }: { children: React.ReactNode }) => {
  const { practiceUser } = usePracticeAuth();
  const [chats, setChats] = useState<PracticeChatSummary[]>([]);
  const [ringedForChatId, setRingedForChatId] = useState<number | null>(null);
  const [dismissedChatId, setDismissedChatId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (!practiceUser || practiceUser.verificationStatus !== "APPROVED") return;
    const res = await fetch(`${API}/api/practice/chats`, { credentials: "include" });
    if (res.ok) setChats(await res.json());
  }, [practiceUser]);

  usePolling(refresh, 5000, !!practiceUser);

  useEffect(() => {
    if (!practiceUser) setChats([]);
  }, [practiceUser]);

  // A direct 1:1 ring takes priority over a group invite if both somehow
  // fire at once (rare — different chats, different notification sources).
  const rawDirect =
    (practiceUser && chats.find((c) => c.callActive && c.callStartedBy !== practiceUser.id)) || null;
  const rawGroup = (practiceUser && chats.find((c) => c.groupCallInvite)) || null;

  const raw: IncomingCallInfo | null = rawDirect
    ? {
        chatId: rawDirect.id,
        displayName: rawDirect.otherUser?.displayName ?? "Someone",
        joinPath: `/speaking-partner/chats/${rawDirect.id}/call`,
        isGroup: false,
      }
    : rawGroup && rawGroup.groupCallInvite
    ? {
        chatId: rawGroup.id,
        displayName: rawGroup.groupCallInvite.hostDisplayName,
        joinPath: `/speaking-partner/group-calls/${rawGroup.groupCallInvite.hostChatId}/call`,
        isGroup: true,
      }
    : null;

  useEffect(() => {
    if (raw) {
      if (raw.chatId !== ringedForChatId) {
        playRing();
        setRingedForChatId(raw.chatId);
        setDismissedChatId(null);
      }
    } else {
      setRingedForChatId(null);
    }
  }, [raw, ringedForChatId]);

  const incomingCall = raw && raw.chatId !== dismissedChatId ? raw : null;

  return (
    <PracticeChatsContext.Provider
      value={{
        chats,
        refresh,
        incomingCall,
        dismissIncomingCall: () => raw && setDismissedChatId(raw.chatId),
      }}
    >
      {children}
    </PracticeChatsContext.Provider>
  );
};

export const usePracticeChats = () => {
  const context = useContext(PracticeChatsContext);
  if (!context) throw new Error("usePracticeChats must be used within a PracticeChatsProvider");
  return context;
};
